<template>
  <h2>{{ t('otherConditions') }}</h2>
  <div class="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
    <OverrideDefaultValueField
      v-slot="{ defaultInputBinds }"
      name="customDeposit"
      :label="t('deposit')"
      type="number"
      :default-value="booking.priceCalculation.defaultDeposit"
      :formatted-default-value="formatCurrency(booking.priceCalculation.defaultDeposit)"
      :disabled="!$can('create', pricingSubject(booking.priceCalculation), 'customDeposit')"
    >
      <Input
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        v-bind="defaultInputBinds"
      />
    </OverrideDefaultValueField>
    <OverrideDefaultValueField
      v-slot="{ defaultInputBinds }"
      name="customPricePerExtraKm"
      :label="t('pricePerExtraKm')"
      type="number"
      :default-value="booking.priceCalculation.defaultPricePerExtraKm"
      :formatted-default-value="formatCurrency(booking.priceCalculation.defaultPricePerExtraKm)"
      :disabled="!$can('create', pricingSubject(booking.priceCalculation), 'customPricePerExtraKm')"
    >
      <Input
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        v-bind="defaultInputBinds"
      />
    </OverrideDefaultValueField>
    <SelectField
      v-if="canEditTaxPercent"
      name="taxPercent"
      :label="t('taxPercent')"
      type="number"
      :options="taxPercentOptions"
    />
  </div>
  <div v-if="hasChanged" class="mt-4 flex justify-end gap-4">
    <CVButton variant="default" :is-loading="isSaving" @click.prevent="onSubmit">
      {{ t('save') }}
    </CVButton>
    <CVButton variant="secondary" :disabled="isSaving" @click.prevent="onCancel">{{
      t('cancel')
    }}</CVButton>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import OverrideDefaultValueField from '@/components/OverrideDefaultValueField.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { useAbility } from '@casl/vue';
import { formatCurrency } from '@/utils/format-numbers';
import CVButton from '@/components/buttons/CVButton.vue';
import Input from '../Input.vue';
import SelectField from '../SelectField.vue';
import { PriceRecalculationMode, type BookingPricingDto } from '@carvia/ros-client-types';
import { getCustomTotalBasePrice, isLongTermPricing, pricingSubject } from '@/utils/pricing.utils';
import { isNonStandardTaxProcedure, resolvePricingTaxPercent } from '@/utils/tax-procedure';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { isAtLeastHandedOver } from '@/utils/price-difference-counterparty';
import { useUpdateBooking } from '@/queries/use-bookings.ts';

const { t } = useI18n();
const { can } = useAbility();
const { confirmAndSave, isBusy } = usePriceChangeConfirmation();
const { isPending: isSavingOtherFields, mutateAsync: updateBooking } = useUpdateBooking();

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const isSaving = computed(() => isBusy.value || isSavingOtherFields.value);

const effectiveTaxPercent = computed(() =>
  resolvePricingTaxPercent(props.booking.priceCalculation.taxPercent, props.booking.taxProcedure),
);

const canEditTaxPercent = computed(
  () =>
    !isNonStandardTaxProcedure(props.booking.taxProcedure) &&
    can('create', pricingSubject(props.booking.priceCalculation), 'taxPercent') &&
    !(isLongTermPricing(props.booking.priceCalculation) && isAtLeastHandedOver(props.booking)),
);

const taxPercentOptions = [
  { label: '0 %', value: 0 },
  { label: '7 %', value: 7 },
  { label: '19 %', value: 19 },
];

const {
  handleSubmit,
  values: formValues,
  setValues,
  setFieldValue,
} = useForm({
  initialValues: {
    customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
    customDeposit: props.booking.priceCalculation.customDeposit,
    customPricePerExtraKm: props.booking.priceCalculation.customPricePerExtraKm,
    taxPercent: effectiveTaxPercent.value,
  },
  validationSchema: yup.object({
    customTotalBasePrice: yup.number().min(0).required().nullable(),
    customDeposit: yup.number().required().nullable(),
    customPricePerExtraKm: yup.number().required().nullable(),
    taxPercent: yup.number().oneOf([0, 7, 19]).required(),
  }),
});

const onCancel = () => {
  setValues({
    customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
    customDeposit: props.booking.priceCalculation.customDeposit,
    customPricePerExtraKm: props.booking.priceCalculation.customPricePerExtraKm,
    taxPercent: effectiveTaxPercent.value,
  });
};

const unchangedValues = ref({
  customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
  customDeposit: props.booking.priceCalculation.customDeposit,
  customPricePerExtraKm: props.booking.priceCalculation.customPricePerExtraKm,
  taxPercent: effectiveTaxPercent.value,
});

const onSubmit = handleSubmit(async (values) => {
  const pricing: BookingPricingDto = {};

  if (values.customTotalBasePrice !== unchangedValues.value.customTotalBasePrice) {
    pricing.customTotalBasePrice = values.customTotalBasePrice;
  }
  if (values.customDeposit !== unchangedValues.value.customDeposit) {
    pricing.customDeposit = values.customDeposit;
  }
  if (values.customPricePerExtraKm !== unchangedValues.value.customPricePerExtraKm) {
    pricing.customPricePerExtraKm = values.customPricePerExtraKm;
  }
  if (values.taxPercent !== unchangedValues.value.taxPercent) {
    pricing.taxPercent = values.taxPercent;
  }

  const taxPercentChanged = values.taxPercent !== unchangedValues.value.taxPercent;

  if (taxPercentChanged) {
    const { status } = await confirmAndSave({
      booking: props.booking,
      updateBookingDto: {
        pricing: {
          ...pricing,
          priceRecalculationMode: PriceRecalculationMode.RECALCULATE,
        },
      },
      allowKeepOldPrice: true,
    });
    if (status !== 'saved') {
      return;
    }
  } else {
    await updateBooking({
      id: props.booking.id,
      booking: {
        pricing: {
          ...pricing,
          priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
        },
      },
    });
  }

  unchangedValues.value = cloneDeep(formValues);
});

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));

watch(effectiveTaxPercent, (taxPercent) => {
  if (formValues.taxPercent === unchangedValues.value.taxPercent) {
    setFieldValue('taxPercent', taxPercent);
    unchangedValues.value.taxPercent = taxPercent;
  }
});
</script>

<i18n lang="json">
{
  "en": {
    "otherConditions": "Other Conditions",
    "totalBasePrice": "Car price (without extras)",
    "deposit": "Deposit",
    "pricePerExtraKm": "Price per extra km",
    "taxPercent": "Tax rate"
  },
  "de": {
    "otherConditions": "Sonstige Konditionen",
    "totalBasePrice": "Fahrzeugpreis (ohne Extras)",
    "deposit": "Kaution",
    "pricePerExtraKm": "Preis pro Extra Km",
    "taxPercent": "Steuersatz"
  },
  "es": {
    "otherConditions": "Otras condiciones",
    "totalBasePrice": "Precio del coche (sin extras)",
    "deposit": "Depósito",
    "pricePerExtraKm": "Precio por km adicional",
    "taxPercent": "Tasa de impuestos"
  }
}
</i18n>
